import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fmtGYD, apiErr, mediaUrl } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { GuyanaDiscountsConnection } from "@/components/GuyanaDiscountsConnection";
import {
  LayoutDashboard, Store, Users, RefreshCw, ScrollText, Plus, LogOut, X, CheckCircle2,
  XCircle, MessageSquare, ShieldAlert, ShieldCheck, TrendingUp, Building2, Globe,
} from "lucide-react";

const NAV = [
  { key: "overview", label: "Platform Overview", Icon: LayoutDashboard },
  { key: "queue", label: "Approval Queue", Icon: Store },
  { key: "merchants", label: "Merchants", Icon: Building2 },
  { key: "sync", label: "Sync Health", Icon: RefreshCw },
  { key: "guyanadiscounts", label: "GuyanaDiscounts.com", Icon: Globe },
  { key: "audit", label: "Audit Logs", Icon: ScrollText },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  return (
    <div className="pos-dark min-h-screen flex">
      <aside className="w-60 border-r border-slate-800 bg-slate-900/50 flex flex-col">
        <div className="p-4 border-b border-slate-800">
          <div className="font-bold text-indigo-400 text-lg">MatrixShopping</div>
          <div className="text-xs text-slate-500">Platform Administration</div>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {NAV.map(({ key, label, Icon }) => (
            <button key={key} data-testid={`admin-nav-${key}`} onClick={() => setTab(key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm touch-btn ${tab === key ? "bg-indigo-600" : "hover:bg-slate-800 text-slate-300"}`}>
              <Icon size={17} /> {label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="text-xs text-slate-400 px-1 mb-2">{user.name}</div>
          <button data-testid="admin-logout" onClick={async () => { await logout(); nav("/login"); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-800 text-slate-400"><LogOut size={16} /> Sign out</button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-800">
          <h1 className="text-lg font-bold">{NAV.find((n) => n.key === tab)?.label}</h1>
          <StatusBadge />
        </div>
        <div className="p-6">
          {tab === "overview" && <Overview />}
          {tab === "queue" && <Queue />}
          {tab === "merchants" && <Merchants />}
          {tab === "sync" && <SyncHealth />}
          {tab === "guyanadiscounts" && <GuyanaDiscountsConnection />}
          {tab === "audit" && <Audit />}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, sub, Icon, tone = "indigo" }) {
  const tones = { indigo: "text-indigo-400", emerald: "text-emerald-400", amber: "text-amber-400", sky: "text-sky-400" };
  return (
    <div className="ms-card p-4 rounded-xl fade-up">
      <div className="flex items-center justify-between"><span className="text-xs text-slate-400">{label}</span>{Icon && <Icon size={16} className={tones[tone]} />}</div>
      <div className="text-2xl font-bold mono mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/reports/platform").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Platform Sales" value={fmtGYD(d.total_platform_sales)} sub={`${d.total_sales_count} POS transactions`} Icon={TrendingUp} tone="emerald" />
        <Stat label="Merchants" value={d.merchant_count} sub={`${d.active_merchants} active`} Icon={Building2} />
        <Stat label="Published Listings" value={d.published_listings} sub={`${d.pending_listings} pending`} Icon={Store} tone="sky" />
        <Stat label="Online Orders" value={d.online_orders} sub="GuyanaDiscounts.com" Icon={ShoppingIcon} tone="amber" />
      </div>
      <div className="ms-card p-4 rounded-xl">
        <h3 className="font-semibold mb-3">Sales by Merchant</h3>
        <table className="w-full text-sm"><thead className="text-slate-400 text-xs"><tr><th className="text-left p-2">Merchant</th><th className="text-right p-2">Transactions</th><th className="text-right p-2">Total</th></tr></thead>
          <tbody>{d.merchant_sales.map((m) => <tr key={m.merchant_id} className="border-t border-slate-800"><td className="p-2">{m.merchant_name}</td><td className="p-2 text-right mono">{m.count}</td><td className="p-2 text-right mono">{fmtGYD(m.total)}</td></tr>)}
            {d.merchant_sales.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-slate-500">No sales yet</td></tr>}</tbody></table>
      </div>
    </div>
  );
}
function ShoppingIcon(props) { return <TrendingUp {...props} />; }

function Queue() {
  const [status, setStatus] = useState("pending_approval");
  const [items, setItems] = useState([]);
  const [note, setNote] = useState(""); const [active, setActive] = useState(null);
  const load = () => api.get("/admin/marketplace/queue", { params: { status } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]);
  const act = async (id, action, body = {}) => { await api.post(`/admin/marketplace/${id}/${action}`, body); setActive(null); setNote(""); load(); };
  const removeImage = async (l, imageId) => {
    if (!window.confirm("Remove this photo from the product and its GuyanaDiscounts listing?")) return;
    try { await api.delete(`/products/${l.pos_product_id}/images/${imageId}`); load(); } catch (e) { window.alert(apiErr(e)); }
  };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending_approval", "changes_requested", "approved", "published", "rejected", "all"].map((s) => (
          <button key={s} data-testid={`queue-filter-${s}`} onClick={() => setStatus(s)} className={`touch-btn text-xs px-3 py-1.5 rounded-lg ${status === s ? "bg-indigo-600" : "ms-card ms-card-hover"}`}>{s.replace("_", " ")}</button>
        ))}
      </div>
      <div className="space-y-3">
        {items.map((l) => (
          <div key={l.id} data-testid={`queue-item-${l.id}`} className="ms-card rounded-xl p-4 fade-up">
            <div className="flex items-start justify-between">
              <div className="flex gap-3">
                {l.snapshot?.images?.[0] && <img src={mediaUrl(l.snapshot.images[0].thumb)} alt="" data-testid={`queue-thumb-${l.id}`} className="w-14 h-14 rounded-lg object-cover border border-slate-700" />}
                <div>
                  <div className="font-semibold">{l.snapshot?.name}</div>
                  <div className="text-xs text-slate-400">{l.merchant_name} · <span className="mono">{fmtGYD(l.snapshot?.price)}</span> · stock {l.snapshot?.quantity}</div>
                  <div className="text-xs text-slate-500 mt-1">{l.snapshot?.description}</div>
                </div>
              </div>
              <span className="text-[11px] px-2 py-0.5 rounded bg-slate-700/50 text-slate-300">{l.status.replace("_", " ")}</span>
            </div>
            {(l.snapshot?.images || []).length > 0 && (
              <div className="flex gap-2 flex-wrap mt-3" data-testid={`queue-gallery-${l.id}`}>
                {l.snapshot.images.map((im, i) => (
                  <div key={im.id} className="relative" data-testid={`queue-image-${im.id}`}>
                    <img src={mediaUrl(im.thumb)} alt="" className={`w-12 h-12 rounded-md object-cover border ${i === 0 ? "border-indigo-400" : "border-slate-700"}`} />
                    <button data-testid={`queue-image-delete-${im.id}`} title="Remove this photo from the listing" onClick={() => removeImage(l, im.id)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-600 hover:bg-rose-500 text-white flex items-center justify-center shadow"><X size={11} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-3 flex-wrap">
              {l.status === "pending_approval" && <>
                <button data-testid={`approve-${l.id}`} onClick={() => act(l.id, "approve")} className="touch-btn text-xs bg-emerald-600 hover:bg-emerald-500 rounded-lg px-3 py-1.5 flex items-center gap-1"><CheckCircle2 size={14} /> Approve</button>
                <button data-testid={`reject-${l.id}`} onClick={() => setActive({ id: l.id, action: "reject" })} className="touch-btn text-xs bg-rose-600 hover:bg-rose-500 rounded-lg px-3 py-1.5 flex items-center gap-1"><XCircle size={14} /> Reject</button>
                <button data-testid={`changes-${l.id}`} onClick={() => setActive({ id: l.id, action: "request-changes" })} className="touch-btn text-xs bg-amber-600 hover:bg-amber-500 rounded-lg px-3 py-1.5 flex items-center gap-1"><MessageSquare size={14} /> Request Changes</button>
              </>}
              {l.status === "approved" && <button data-testid={`publish-${l.id}`} onClick={() => act(l.id, "publish")} className="touch-btn text-xs bg-indigo-600 hover:bg-indigo-500 rounded-lg px-3 py-1.5">Publish to GuyanaDiscounts.com</button>}
              {l.status === "published" && <button data-testid={`pause-${l.id}`} onClick={() => act(l.id, "pause")} className="touch-btn text-xs bg-amber-600 hover:bg-amber-500 rounded-lg px-3 py-1.5">Pause</button>}
            </div>
            {active?.id === l.id && (
              <div className="mt-3 flex gap-2">
                <input data-testid="decision-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / note to merchant" className="inp" />
                <button data-testid="decision-confirm" onClick={() => act(l.id, active.action, { note })} className="touch-btn bg-slate-700 rounded-lg px-4 text-sm">Send</button>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="ms-card rounded-xl p-8 text-center text-slate-500">Nothing here</div>}
      </div>
    </div>
  );
}

function Merchants() {
  const [merchants, setMerchants] = useState([]);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", address: "" });
  const load = () => api.get("/merchants").then((r) => setMerchants(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (m) => { await api.post(`/merchants/${m.id}/${m.status === "active" ? "suspend" : "reactivate"}`); load(); };
  const add = async () => { await api.post("/merchants", f); setAdding(false); setF({ name: "", phone: "", address: "" }); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><button data-testid="merchant-add" onClick={() => setAdding(true)} className="touch-btn bg-indigo-600 hover:bg-indigo-500 rounded-lg px-4 py-2 text-sm flex items-center gap-2"><Plus size={16} /> Add Merchant</button></div>
      <div className="ms-card rounded-xl overflow-hidden">
        <table className="w-full text-sm"><thead className="bg-slate-900/60 text-slate-400 text-xs"><tr><th className="text-left p-3">Merchant</th><th className="text-left p-3">Phone</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr></thead>
          <tbody>{merchants.map((m) => <tr key={m.id} data-testid={`merchant-row-${m.id}`} className="border-t border-slate-800"><td className="p-3 font-medium">{m.name}</td><td className="p-3 text-slate-400 mono">{m.phone}</td>
            <td className="p-3">{m.status === "active" ? <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 inline-flex items-center gap-1"><ShieldCheck size={12} /> Active</span> : <span className="text-[11px] px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 inline-flex items-center gap-1"><ShieldAlert size={12} /> Suspended</span>}</td>
            <td className="p-3 text-right"><button data-testid={`merchant-toggle-${m.id}`} onClick={() => toggle(m)} className={`text-xs ${m.status === "active" ? "text-rose-400" : "text-emerald-400"}`}>{m.status === "active" ? "Suspend" : "Reactivate"}</button></td></tr>)}</tbody></table>
      </div>
      {adding && <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setAdding(false)}>
        <div className="ms-card p-5 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center"><h3 className="font-bold">New Merchant</h3><button onClick={() => setAdding(false)}><X size={18} /></button></div>
          <input data-testid="new-merchant-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Business name" className="inp" />
          <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" className="inp" />
          <input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Address" className="inp" />
          <button data-testid="new-merchant-save" disabled={!f.name} onClick={add} className="w-full touch-btn bg-indigo-600 rounded-lg py-2 text-sm disabled:opacity-50">Create Merchant</button>
        </div>
      </div>}
    </div>
  );
}

function SyncHealth() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/sync/health").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Stat label="Registers" value={d.total_registers} sub={`${d.active_registers} active`} Icon={RefreshCw} />
        <Stat label="Failed Records" value={d.failed_records} tone={d.failed_records ? "amber" : "emerald"} Icon={ShieldAlert} />
        <Stat label="Recent Events" value={d.recent_events.length} tone="sky" />
      </div>
      <div className="ms-card rounded-xl overflow-hidden">
        <div className="p-3 border-b border-slate-800 font-semibold text-sm">Registers</div>
        <table className="w-full text-sm"><thead className="bg-slate-900/60 text-slate-400 text-xs"><tr><th className="text-left p-3">Register</th><th className="text-left p-3">Merchant</th><th className="text-left p-3">Status</th><th className="text-left p-3">Last Sync</th></tr></thead>
          <tbody>{d.registers.map((r) => <tr key={r.id} className="border-t border-slate-800"><td className="p-3 mono">{r.register_number} · {r.name}</td><td className="p-3">{r.merchant_name}</td>
            <td className="p-3">{r.active ? <span className="text-emerald-400 text-xs">● Active</span> : <span className="text-slate-500 text-xs">○ Inactive</span>}</td>
            <td className="p-3 text-slate-400 text-xs">{r.last_sync_at ? new Date(r.last_sync_at).toLocaleString() : "Never"}</td></tr>)}</tbody></table>
      </div>
    </div>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setLogs(r.data)); }, []);
  return (
    <div className="ms-card rounded-xl overflow-hidden">
      <table className="w-full text-xs"><thead className="bg-slate-900/60 text-slate-400"><tr><th className="text-left p-2">Action</th><th className="text-left p-2">User</th><th className="text-left p-2">Role</th><th className="text-left p-2">Record</th><th className="text-left p-2">When</th></tr></thead>
        <tbody>{logs.map((l) => <tr key={l.id} className="border-t border-slate-800"><td className="p-2 font-medium">{l.action}</td><td className="p-2 text-slate-400">{l.user_email}</td><td className="p-2 capitalize">{(l.role || "").replace("_", " ")}</td><td className="p-2 text-slate-500">{l.record_type} {l.record_id?.slice(0, 8)}</td><td className="p-2 text-slate-500">{new Date(l.created_at).toLocaleString()}</td></tr>)}
          {logs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No audit records</td></tr>}</tbody></table>
    </div>
  );
}
